import Joi from 'joi';
import { ModelEntityType } from '../model/input/types';
import { RenderViewCsrdMaterialityMatrixParams } from './component-types/types-render-view-csrd-matriality-matrix';
import { RenderViewHoldingsHistogram } from './component-types/types-render-view-holdings-histogram';
import { RenderViewImpactChartParams } from './component-types/types-render-view-impact-chart';
import { RenderViewNetImpactLensParams } from './component-types/types-render-view-lenses';
import { RenderViewSdgAlignmentsParams } from './component-types/types-render-view-sdg-alignments';
import { RenderViewTopHoldingsParams } from './component-types/types-render-view-top-holdings';
import { RenderViewValuesAlignmentParams } from './component-types/types-render-view-values-alignment';

export enum RenderFileType {
  PNG = 'png',
  PDF = 'pdf',
}

/**
 * The default state always needs to be falsy, i.e. we only supply those values that we want to be different from
 * the default behavior. All falsy/undefined values will be completely left out of the urls.
 */
export interface RenderOptions {
  version: string | undefined;
  release: string | null | undefined;
  isShareImg: boolean;
  renderFileType: RenderFileType;

  /**
   * The "items" array is part of an old logic and is here to support a deprecated data fetching pattern, where
   * RenderView had centralized score fetching. Don't add new components that use this "items" array, and instead
   * provide the item/items in componentParams
   */
  items: RenderItem[] | undefined;

  width: number | undefined;
  height: number | undefined;
  showControlUi: boolean;
  componentParams: RenderViewComponentParams[] | undefined;
  topMargin: number | undefined;
}

// TODO decouple the options for UI render views, API /profile endpoint renders and share images.
export interface RenderItem<Type = ModelEntityType> {
  index: number;
  resultId: number | undefined;
  type: Type;
  id: string;
  name?: string;
  color?: string;

  renderProfileData?: string; // Quick hack for /profile endpoint, see packToStringWithoutValueChains
  startDate?: string;
  endDate?: string;
}

export interface RenderViewQueryParams {
  data?: string;
}

export enum RenderViewComponentType {
  IMPACT_CHART = 'IMPACT_CHART',
  VALUES_ALIGNMENT = 'VALUES_ALIGNMENT',
  IMPACT_COMPARISONS = 'IMPACT_COMPARISONS',
  TOP_HOLDINGS = 'TOP_HOLDINGS',
  HOLDINGS_HISTOGRAM = 'HOLDINGS_HISTOGRAM',
  LENS_NET_IMPACT = 'LENS_NET_IMPACT',
  SDG_ALIGNMENTS = 'SDG_ALIGNMENTS',
  CSRD_MATERIALITY_MATRIX = 'CSRD_MATERIALITY_MATRIX',
}

export type ComponentParamsOfType<T extends RenderViewComponentType> = Extract<
  RenderViewComponentParams,
  { componentType: T }
>;

export type RenderViewComponentParams =
  | RenderViewImpactChartParams
  | RenderViewValuesAlignmentParams
  | RenderViewTopHoldingsParams
  | RenderViewHoldingsHistogram
  | RenderViewNetImpactLensParams
  | RenderViewSdgAlignmentsParams
  | RenderViewCsrdMaterialityMatrixParams;

export interface RenderViewComponentParamsBase {
  componentType: RenderViewComponentType;
  order: number;
}

export const renderViewComponentParamsBaseSchema: Joi.StrictSchemaMap<RenderViewComponentParamsBase> = {
  componentType: Joi.string()
    .valid(...Object.values(RenderViewComponentType))
    .required(),
  order: Joi.number().required(),
};

export interface RenderViewChartTitleParams {
  chartTitle?: string;
  hideTitle?: boolean;
}
